import { useQuery } from "@tanstack/react-query";
import { erc20Abi, type Address } from "viem";

import { ERC4626_ABI, SUPPLY_VAULT } from "@/config/vault";
import { USDC } from "@/config/tokens";
import { buildPublicClient } from "@/lib/wallet/clients";

export type VaultPosition = {
  /** Vault shares held by the user. */
  readonly shares: bigint;
  /** Current USDC value of those shares (raw 1e6). */
  readonly suppliedRaw: bigint;
  /** USDC allowance the user has granted the vault (raw 1e6). */
  readonly allowanceRaw: bigint;
};

export type VaultPositionQuery = {
  readonly position: VaultPosition | null;
  readonly isLoading: boolean;
  readonly refetch: () => void;
};

export const useVaultPosition = (
  account: Address | undefined,
): VaultPositionQuery => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["vault-position", SUPPLY_VAULT.address, account],
    enabled: Boolean(account),
    queryFn: async (): Promise<VaultPosition> => {
      const client = buildPublicClient();
      const owner = account as Address;
      // shares + allowance are independent — one multicall. convertToAssets
      // needs the share balance, so it follows in a second round.
      const [shares, allowanceRaw] = await client.multicall({
        allowFailure: false,
        contracts: [
          {
            address: SUPPLY_VAULT.address,
            abi: ERC4626_ABI,
            functionName: "balanceOf",
            args: [owner],
          },
          {
            address: USDC.address,
            abi: erc20Abi,
            functionName: "allowance",
            args: [owner, SUPPLY_VAULT.address],
          },
        ],
      });
      const suppliedRaw = await client.readContract({
        address: SUPPLY_VAULT.address,
        abi: ERC4626_ABI,
        functionName: "convertToAssets",
        args: [shares],
      });
      return { shares, suppliedRaw, allowanceRaw };
    },
  });

  return {
    position: data ?? null,
    isLoading,
    refetch: () => {
      void refetch();
    },
  };
};
